/**
 * @fileoverview Recovery Key Backup Component - Display and backup 24-word recovery phrase
 *
 * This component provides a UI for:
 * 1. Displaying the 24-word recovery phrase
 * 2. Copying to clipboard
 * 3. Printing the recovery key
 * 4. Downloading as text file
 * 5. Confirmation checklist
 *
 * @module components/RecoveryKeyBackup
 */

import { useState } from 'react';
import {
  Copy,
  Download,
  Printer,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';

import { createLogger } from '@xanectech/lib';

const logger = createLogger('Auth - RecoveryKeyBackup');

interface RecoveryKeyBackupProps {
  recoveryKey: string[];
  onConfirm?: () => void;
  onCancel?: () => void;
  requireConfirmation?: boolean;
}

export function RecoveryKeyBackup({
  recoveryKey,
  onConfirm,
  onCancel,
  requireConfirmation = true,
}: RecoveryKeyBackupProps) {
  const [copied, setCopied] = useState(false);
  const [checklist, setChecklist] = useState({
    written: false,
    stored: false,
    understood: false,
  });

  const allChecked =
    checklist.written && checklist.stored && checklist.understood;

  // Copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(recoveryKey.join(' '));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error('Failed to copy:', err as Error);
    }
  };

  // Download as text file
  const handleDownload = () => {
    const content = `TrueID.Locker Recovery Key\n${'='.repeat(50)}\n\nIMPORTANT: Store this recovery key in a secure location.\nThis is the only way to recover your data if you lose access to all your devices.\n\nRecovery Key:\n${recoveryKey.map((word, i) => `${i + 1}. ${word}`).join('\n')}\n\n${'='.repeat(50)}\nGenerated: ${new Date().toISOString()}\n`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trueid-locker-recovery-key-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Print recovery key
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>TrueID.Locker Recovery Key</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
            }
            h1 {
              color: #4F46E5;
              border-bottom: 3px solid #4F46E5;
              padding-bottom: 10px;
            }
            .warning {
              background: #FEF3C7;
              border-left: 4px solid #F59E0B;
              padding: 15px;
              margin: 20px 0;
            }
            .recovery-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 10px;
              margin: 30px 0;
              padding: 20px;
              border: 2px solid #E5E7EB;
              border-radius: 8px;
            }
            .word-item {
              font-family: 'Courier New', monospace;
              padding: 8px;
              background: #F9FAFB;
              border-radius: 4px;
            }
            .word-number {
              color: #9CA3AF;
              margin-right: 8px;
            }
            .instructions {
              margin: 30px 0;
              line-height: 1.6;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #E5E7EB;
              color: #6B7280;
              font-size: 12px;
            }
            @media print {
              body { padding: 20px; }
              .warning { break-inside: avoid; }
              .recovery-grid { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>🔐 TrueID.Locker Recovery Key</h1>
          
          <div class="warning">
            <strong>⚠️ IMPORTANT SECURITY NOTICE</strong><br>
            This recovery key is the ONLY way to recover your encrypted data if you lose access to all your devices.
            Store it in a secure location and never share it with anyone.
          </div>

          <div class="recovery-grid">
            ${recoveryKey
              .map(
                (word, i) => `
              <div class="word-item">
                <span class="word-number">${i + 1}.</span>
                <strong>${word}</strong>
              </div>
            `,
              )
              .join('')}
          </div>

          <div class="instructions">
            <h3>Storage Instructions:</h3>
            <ul>
              <li>Store this paper in a fireproof safe or safety deposit box</li>
              <li>Consider making a second copy and storing it in a different secure location</li>
              <li>Never take a photo of this recovery key</li>
              <li>Never store it digitally without encryption</li>
              <li>Never share it with anyone, including TrueID.Locker support</li>
            </ul>
          </div>

          <div class="footer">
            Generated: ${new Date().toLocaleString()}<br>
            TrueID.Locker - Privacy-First Data Management
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Toggle checklist item
  const toggleCheck = (item: keyof typeof checklist) => {
    setChecklist((prev) => ({ ...prev, [item]: !prev[item] }));
  };

  return (
    <div className='space-y-6'>
      {/* Warning */}
      <div className='bg-warning/10 dark:bg-warning/5 border border-warning/30 rounded-lg p-4'>
        <div className='flex gap-3'>
          <AlertTriangle className='w-5 h-5 text-warning flex-shrink-0 mt-0.5' />
          <div>
            <h4 className='text-sm font-medium text-yellow-800 dark:text-yellow-300'>
              Critical: Save Your Recovery Key
            </h4>
            <p className='text-sm text-yellow-700 dark:text-yellow-400 mt-1'>
              This 24-word recovery key is the <strong>only way</strong> to
              recover your data if you lose all your devices. Store it securely
              offline.
            </p>
          </div>
        </div>
      </div>

      {/* Recovery key display */}
      <div className='bg-muted border-2 border-border rounded-lg p-6'>
        <div className='grid grid-cols-3 gap-3 font-mono text-sm'>
          {recoveryKey.map((word, index) => (
            <div
              key={index}
              className='flex items-center bg-background rounded px-3 py-2 border border-border'
            >
              <span className='text-muted-foreground mr-2 min-w-[2rem]'>
                {index + 1}.
              </span>
              <span className='font-semibold text-foreground'>{word}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Action buttons */}
      <div className='grid grid-cols-3 gap-3'>
        <button
          onClick={handleCopy}
          className='flex items-center justify-center gap-2 px-4 py-2 bg-card border border-border text-foreground/90 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
        >
          {copied ? (
            <>
              <CheckCircle className='w-4 h-4 text-green-600' />
              Copied!
            </>
          ) : (
            <>
              <Copy className='w-4 h-4' />
              Copy
            </>
          )}
        </button>

        <button
          onClick={handleDownload}
          className='flex items-center justify-center gap-2 px-4 py-2 bg-card border border-border text-foreground/90 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
        >
          <Download className='w-4 h-4' />
          Download
        </button>

        <button
          onClick={handlePrint}
          className='flex items-center justify-center gap-2 px-4 py-2 bg-card border border-border text-foreground/90 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
        >
          <Printer className='w-4 h-4' />
          Print
        </button>
      </div>

      {/* Storage instructions */}
      <div className='bg-info/10 dark:bg-info/5 border border-info/30 rounded-lg p-4'>
        <h4 className='text-sm font-medium text-blue-900 dark:text-blue-300 mb-3'>
          Recommended Storage Methods:
        </h4>
        <ul className='space-y-2 text-sm text-blue-800 dark:text-blue-400'>
          <li className='flex items-start gap-2'>
            <span className='text-success mt-0.5'>✓</span>
            <span>Write it down on paper and store in a fireproof safe</span>
          </li>
          <li className='flex items-start gap-2'>
            <span className='text-success mt-0.5'>✓</span>
            <span>
              Use a hardware-encrypted USB drive in a safety deposit box
            </span>
          </li>
          <li className='flex items-start gap-2'>
            <span className='text-success mt-0.5'>✓</span>
            <span>
              Store in a reputable password manager with offline backup
            </span>
          </li>
          <li className='flex items-start gap-2'>
            <span className='text-destructive mt-0.5'>✗</span>
            <span>Don't take a photo or screenshot</span>
          </li>
          <li className='flex items-start gap-2'>
            <span className='text-destructive mt-0.5'>✗</span>
            <span>Don't email it to yourself</span>
          </li>
          <li className='flex items-start gap-2'>
            <span className='text-destructive mt-0.5'>✗</span>
            <span>Don't store it in cloud storage unencrypted</span>
          </li>
        </ul>
      </div>

      {/* Confirmation checklist */}
      {requireConfirmation && (
        <div className='space-y-3 bg-card border border-border rounded-lg p-4'>
          <h4 className='font-medium text-foreground'>
            Confirm you've secured your recovery key:
          </h4>

          <label className='flex items-start gap-3 cursor-pointer'>
            <input
              type='checkbox'
              checked={checklist.written}
              onChange={() => toggleCheck('written')}
              className='mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
            />
            <span className='text-sm text-foreground/90'>
              I have written down or saved my recovery key in a secure location
            </span>
          </label>

          <label className='flex items-start gap-3 cursor-pointer'>
            <input
              type='checkbox'
              checked={checklist.stored}
              onChange={() => toggleCheck('stored')}
              className='mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
            />
            <span className='text-sm text-foreground/90'>
              I understand that losing this recovery key means permanent data
              loss if I lose all my devices
            </span>
          </label>

          <label className='flex items-start gap-3 cursor-pointer'>
            <input
              type='checkbox'
              checked={checklist.understood}
              onChange={() => toggleCheck('understood')}
              className='mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
            />
            <span className='text-sm text-foreground/90'>
              I understand that TrueID.Locker cannot recover my data without
              this recovery key
            </span>
          </label>
        </div>
      )}

      {/* Action buttons */}
      {requireConfirmation && (onConfirm || onCancel) && (
        <div className='flex justify-between gap-4'>
          {onCancel && (
            <button
              onClick={onCancel}
              className='px-6 py-2 text-foreground/90 hover:text-gray-900 dark:hover:text-gray-100 font-medium'
            >
              Back
            </button>
          )}
          {onConfirm && (
            <button
              onClick={onConfirm}
              disabled={!allChecked}
              className='px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors'
            >
              I've Secured My Recovery Key
            </button>
          )}
        </div>
      )}
    </div>
  );
}
